import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, model, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faBarcode } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { SignalFieldBaseDirective } from '../signal-field-base.directive';

/**
 * Low-level number input for signal forms.
 *
 * The first field whose value is not a string, so it extends the generic
 * `SignalFieldBaseDirective` directly and brings its own `value` and input handler.
 * Everything else — the contract inputs, the bootstrap validation state — is
 * inherited unchanged:
 *
 * ```html
 * <pace-core-number-field [formField]="signalForm.numberValue" ariaLabel="Distance" />
 * ```
 *
 * An empty field is `null` rather than `undefined`, both because that is what the
 * store slices carry (the root store's `strictStateSerializability` check rejects
 * explicitly undefined values) and because signal forms type their numeric paths as
 * `number | string | null`.
 *
 * Unlike the date and time fields, the bounds need no inputs of their own: `min` and
 * `max` are part of the `FormUiControl` contract, so the schema's `min()`/`max()`
 * rules reach the native input on their own.
 *
 * The +/- variant of the reactive-forms widgets (`NumberStepperComponent`) is a
 * separate component there and would be one here too; this covers `NumberInputComponent`.
 */
@Component({
    selector: 'pace-number-field',
    templateUrl: './number-field.component.html',
    styleUrls: ['./number-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FaIconComponent, TranslateModule],
})
export class NumberFieldComponent extends SignalFieldBaseDirective<number | null> {
    /** the edited value; `null` while the field is empty */
    public readonly value = model<number | null>(null);

    // --- `FormUiControl` inputs, all bound automatically by `[formField]` -----

    public readonly min = input<number | undefined>(undefined);
    public readonly max = input<number | undefined>(undefined);

    // --- own inputs ----------------------------------------------------------

    public readonly placeholder = input('');

    /** optional unit label rendered after the input, e.g. 'KW' */
    public readonly unit = input('');

    /** native `step`; left unset the browser allows whole numbers only */
    public readonly step = input<number | 'any' | undefined>(undefined);

    // --- barcode scanning ----------------------------------------------------

    public readonly scanFn = input<(() => Observable<number | null>) | undefined>(undefined);

    /** shows the scan button */
    public readonly scannable = computed(() => this.scanFn() !== undefined);

    /** the parsed scan result, for consumers that need to react beyond the value change */
    public readonly scanResults = output<number | null>();

    public readonly faBarcode = faBarcode;

    private readonly destroyRef = inject(DestroyRef);

    /**
     * A number input reports an empty string both when it is cleared and while the
     * typed text is not a valid number, and `valueAsNumber` is NaN in both cases —
     * either way the field has no value, so it publishes `null` and lets the
     * schema's `required` rule speak.
     *
     * @param event the native input event
     */
    public onInput(event: Event): void {
        const parsed = (<HTMLInputElement>event.target).valueAsNumber;
        this.value.set(Number.isNaN(parsed) ? null : parsed);
    }

    /**
     * Opens the scanner and writes the parsed result into the field. The value is set
     * through the same model the user's typing goes through, so the bound field
     * revalidates exactly as it would after manual input; `touched` is set too,
     * because a scan is an interaction even though no blur happens.
     */
    public scan(): void {
        const fn = this.scanFn();
        if (!fn) {
            return;
        }
        fn()
            .pipe(
                take(1),
                tap((result) => {
                    this.value.set(result);
                    this.touched.set(true);
                    this.scanResults.emit(result);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }
}
